import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, MessageCircle, Twitter } from 'lucide-react';

export default function Footer() {
  return (
    <>
      <style>{`
        .footer { background: var(--charcoal-dark); }
        .footer-top { padding: 72px 0 56px; border-bottom: 1px solid rgba(255,255,255,0.08); }
        .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1.3fr; gap: 52px; }
        .footer-logo { height: 44px; width: auto; object-fit: contain; margin-bottom: 16px; }
        .footer-tagline {
          font-family: var(--font-display); font-size: 16px; font-style: italic;
          color: var(--gold); margin-bottom: 16px;
        }
        .footer-about { font-size: 13px; line-height: 1.8; color: rgba(255,255,255,0.45); margin-bottom: 24px; }
        .footer-socials { display: flex; gap: 10px; }
        .fsoc { width: 36px; height: 36px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.4); cursor: pointer; transition: all 0.22s; }
        .fsoc:hover { background: var(--gold); color: var(--charcoal-dark); border-color: var(--gold); }
        .footer-col-title { font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 20px; }
        .footer-links { display: flex; flex-direction: column; gap: 10px; }
        .footer-links a { font-size: 13px; color: rgba(255,255,255,0.45); transition: color 0.2s; }
        .footer-links a:hover { color: var(--gold-hover); }
        .fci { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .fci svg { color: var(--gold); flex-shrink: 0; margin-top: 2px; }
        .fci-text { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.5; }
        .fci-text a { color: rgba(255,255,255,0.6); transition: color 0.2s; }
        .fci-text a:hover { color: var(--gold-hover); }
        .footer-book-box { background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.2); border-radius: var(--radius-sm); padding: 20px; margin-top: 20px; }
        .fbb-title { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold); margin-bottom: 8px; }
        .fbb-text { font-size: 12px; color: rgba(255,255,255,0.45); line-height: 1.6; margin-bottom: 12px; }
        .fbb-btn { display: block; text-align: center; padding: 10px; background: var(--gold); color: var(--charcoal-dark); font-size: 12px; font-weight: 700; transition: background 0.2s; border-radius: var(--radius-sm); }
        .fbb-btn:hover { background: var(--gold-hover); }
        .footer-bottom { padding: 18px 0; }
        .fbot-inner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
        .fbot-copy { font-size: 12px; color: rgba(255,255,255,0.3); }
        .fbot-links { display: flex; gap: 20px; }
        .fbot-links a { font-size: 12px; color: rgba(255,255,255,0.3); transition: color 0.2s; }
        .fbot-links a:hover { color: var(--gold-hover); }
        @media (max-width: 960px) { .footer-grid { grid-template-columns: 1fr 1fr; gap: 36px; } }
        @media (max-width: 560px) { .footer-grid { grid-template-columns: 1fr; } .fbot-inner { flex-direction: column; text-align: center; } }
      `}</style>

      <footer className="footer">
        <div className="footer-top">
          <div className="container">
            <div className="footer-grid">
              <div>
                <img src="/logo.png" alt="Trinity Accounting Consultants" className="footer-logo" />
                <div className="footer-tagline">Let's be wings of your Dreams.</div>
                <p className="footer-about">
                  Trinity Accounting Consultants is an independent firm providing value-added 
                  tax and advisory solutions to individuals and businesses in Toowoomba and across Australia.
                </p>
                <div className="footer-socials">
                  <div className="fsoc"><Facebook size={14} /></div>
                  <div className="fsoc"><MessageCircle size={14} /></div>
                  <div className="fsoc"><Twitter size={14} /></div>
                </div>
              </div>

              <div>
                <div className="footer-col-title">About Us</div>
                <div className="footer-links">
                  <Link to="/why-choose-us">Why Choose Us</Link>
                  <Link to="/who-we-are">Who We Are</Link>
                  <a href="https://www.mytcs.com.au/forms/authorization-form/" target="_blank" rel="noreferrer">Forms</a>
                </div>
              </div>

              <div>
                <div className="footer-col-title">Services</div>
                <div className="footer-links">
                  {['ASIC Lodgments','Tax Returns','Tax Planning','Bookkeeping','Business Structuring','Software Training'].map(s => (
                    <Link key={s} to="/services">{s}</Link>
                  ))}
                </div>
              </div>

              <div>
                <div className="footer-col-title">Contact Us</div>
                <div className="fci">
                  <Mail size={14} />
                  <div className="fci-text"><a href="mailto:admin@trinityconsultingservices.com.au">admin@trinityconsultingservices.com.au</a></div>
                </div>
                <div className="fci">
                  <Phone size={14} />
                  <div className="fci-text"><a href="tel:0469847803">0469 847 803</a></div>
                </div>
                <div className="fci">
                  <MapPin size={14} />
                  <div className="fci-text">Toowoomba, Queensland, Australia</div>
                </div>
                <div className="footer-book-box">
                  <div className="fbb-title">Book Appointment</div>
                  <p className="fbb-text">Face to face or online appointments available for 2025.</p>
                  <a href="https://www.mytcs.com.au/" target="_blank" rel="noreferrer" className="fbb-btn">Book Now →</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="container">
            <div className="fbot-inner">
              <div className="fbot-copy">© {new Date().getFullYear()} Trinity Accounting Consultants. All rights reserved.</div>
              <div className="fbot-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
